import { useRef, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import ConvertedCurrency from "@/pages/deals/components/commercials/ConvertedCurrency";
import {
  formatCurrency,
  getPricePerUnitSuffix,
  recordedTotalBeforeDiscount
} from "../consts";
import Text from "@/components/ui/text";
import { ContractRevenueItem } from "@/pages/contracts/types";
import { useTranslation } from "react-i18next";

interface PricingCellProps {
  item: ContractRevenueItem;
  isPricingPerPeriod: boolean;
  isFlexiblePricing: boolean;
  periodInMonths: number;
  currency: string;
  showConversion: boolean;
  rate: number | undefined;
  accountCurrencySymbol: string;
  // The sign formatCurrency should print, already resolved against the account's beside it.
  currencySignOverride?: string;
}

// Resolve the amount shown in the Pricing column.
//   flexible      → the entered per-unit price
//   per-period    → the whole-period total (flat rate has no per-unit notion)
//   per-unit      → the stored per-single-unit price, or the one a legacy row
//                   written before that column was persisted implies. A stored
//                   price is net of nothing and covers one unit for one month,
//                   so recovering one has to undo the discount and divide by
//                   the period as well as the quantity — dividing by quantity
//                   alone overstates it on any multi-month term.
const getPricingAmount = (
  item: ContractRevenueItem,
  totalLocal: number | null,
  isFlexiblePricing: boolean,
  isPricingPerPeriod: boolean,
  periodInMonths: number
): number | null => {
  if (isFlexiblePricing) return item.pricePerUnit;
  if (isPricingPerPeriod) return totalLocal;
  if (item.pricePerUnit != null) return item.pricePerUnit;
  const beforeDiscount = recordedTotalBeforeDiscount(item);
  // A downsell divides a negative total by a negative quantity — the quotient is
  // the correct positive unit price, so only zero has to be guarded against.
  const divisor = (item.quantity ?? 0) * (periodInMonths || 1);
  if (beforeDiscount != null && divisor !== 0) {
    return beforeDiscount / divisor;
  }
  return null;
};

const PricingCell: React.FC<PricingCellProps> = ({
  item,
  isPricingPerPeriod,
  isFlexiblePricing,
  periodInMonths,
  currency,
  showConversion,
  rate,
  accountCurrencySymbol,
  currencySignOverride
}) => {
  const { t } = useTranslation();
  // The whole cell is the hover target, though only the suffix clips.
  const suffixRef = useRef<HTMLSpanElement>(null);
  const [isSuffixClipped, setIsSuffixClipped] = useState(false);

  // Measured on hover: the column fixes the box, so a ResizeObserver never fires.
  const measureSuffix = () => {
    const element = suffixRef.current;
    setIsSuffixClipped(!!element && element.scrollWidth > element.clientWidth);
  };

  const totalLocal = item.totalLocal ?? item.total;
  const pricingAmount = getPricingAmount(
    item,
    totalLocal,
    isFlexiblePricing,
    isPricingPerPeriod,
    periodInMonths
  );

  const suffix = isFlexiblePricing
    ? t("contractTerms.perUnit")
    : isPricingPerPeriod
      ? null
      : getPricePerUnitSuffix(item.customBillingFrequency, t);
  if (pricingAmount == null) {
    return null;
  }

  const cell = (
    <div
      className="flex items-center gap-1 min-w-0"
      onPointerEnter={measureSuffix}
    >
      <Text variant="body" className="flex-shrink-0 whitespace-nowrap">
        {formatCurrency(pricingAmount, currency, undefined, currencySignOverride)}
      </Text>
      {showConversion && pricingAmount > 0 && (
        <div className="flex-shrink-0 whitespace-nowrap">
          <ConvertedCurrency
            amount={pricingAmount}
            rate={rate}
            accountCurrencySymbol={accountCurrencySymbol}
            variant="bodyTertiary"
          />
        </div>
      )}
      {suffix && (
        <span ref={suffixRef} className="truncate min-w-0">
          <Text as="span" variant="body">
            {suffix}
          </Text>
        </span>
      )}
    </div>
  );

  if (!suffix) return cell;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{cell}</TooltipTrigger>
      {isSuffixClipped && (
        <TooltipContent enableMarkdown={false}>
          {`${formatCurrency(
            pricingAmount,
            currency,
            undefined,
            currencySignOverride
          )} ${suffix}`}
        </TooltipContent>
      )}
    </Tooltip>
  );
};

export default PricingCell;
